import { QueryFunctionContext, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { ActionItemSummaryDto, FetchError, queryKeys, UrlUtilities, useAccessToken, type QueryParams } from "src/http";

const useGetPlanSubitemsRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function getPlanSubitemsRequest({
    pageParam: actionItemGuid,
    queryKey,
  }: QueryFunctionContext<ReturnType<typeof queryKeys.plans.getPlanSubitemsWithOptions>, string>): Promise<
    ActionItemSummaryDto[]
  > {
    const [{ queryParams }] = queryKey;
    const accessToken = await getAccessToken();

    // If this is the first "page" just return an empty array.
    if (actionItemGuid === undefined) {
      return [];
    }

    const url = new URL(`${configuration.apiRootUrl}/action-items/${actionItemGuid}/subitems`);
    UrlUtilities.appendQueryParams(url, queryParams);

    const response = await fetch(url.href, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { getPlanSubitemsRequest };
};

const useGetPlanSubitems = (planGuid: string | undefined, queryParams?: QueryParams) => {
  const { getPlanSubitemsRequest } = useGetPlanSubitemsRequest();
  const queryClient = useQueryClient();

  return useInfiniteQuery({
    queryKey: queryKeys.plans.getPlanSubitemsWithOptions(planGuid, queryParams),
    queryFn: getPlanSubitemsRequest,
    enabled: planGuid !== undefined,
    onSuccess: (data) => {
      data.pages.forEach((page) =>
        page.forEach((actionItem) =>
          queryClient.setQueryData(queryKeys.actionItems.summary(actionItem.guid), actionItem),
        ),
      );
    },
  });
};

export { useGetPlanSubitems };
